/**
 * Navigation: filter tabs across the top (All · Days · saved views · +, then ✓ Done), a left
 * column on desktop (Home, Notes, Search, the Pages tree; Archive, Trash, you and the sync
 * status at the bottom), and on phones a floating bar with Home · Notes · Pages · Chat and the
 * + beside it. One drag-and-drop context spans the whole shell so cards and rows can be dropped
 * on the tabs or the Pages tree.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Logo } from './Logo'
import { SIDEBAR_DEFAULT, SIDEBAR_MAX, SIDEBAR_MIN, setSidebarWidth, sidebarWidth } from '../lib/sidebar'
import { viewVisibleTo, type SavedView } from '@terrazzo/core'
import { useLocation, navigate } from '../lib/router'
import { useDerived, useSyncStatus, useMe } from '../store/hooks'
import { store } from '../store/store'
import { useKeyboardOpen } from '../lib/viewport'
import { useAnyEditing } from '../store/ui'
import { TreeDndProvider, useTreeDnd } from '../dnd/TreeDnd'
import { useDroppable } from '@dnd-kit/core'
import { IconBoard, IconSearch, IconNotes, IconSettings, IconArchive, IconTrash, IconPages, IconRedo, IconUndo, IconBell, IconChat } from './Icons'
import { useUnreadActivity } from '../lib/activity'
import { startNewMarks } from '../lib/newMark'
import { StatusButton } from './Connections'
import { redo, undo, useUndoState } from '../store/undo'
import { Toasts } from './Toasts'
import { ChartDefs } from './ChartDefs'
import { SheetHost } from './SheetHost'
import { PagesTree } from './PagesTree'
import { TabBar, isTabRoute, useActiveTab } from './TabBar'
import { FindButton, type FindState } from './Find'
import { AddButton } from './Fab'
import { runAddButton } from '../lib/addButton'
import { useIsDesktop, useTouchDevice } from '../lib/touch'

/** Saved tabs after the built-ins: top-level items with props.view, shared or my own. */
export function useSavedViews(): string[] {
  const me = useMe().user?.id
  return useDerived(
    () =>
      (store.tree.childrenOf.get(null) ?? []).filter((id) => {
        const it = store.get(id)
        return !!it && !!it.props.view && !store.tree.inTrash.has(id) && !store.tree.inArchive.has(id) && viewVisibleTo(it.props.view as SavedView, me)
      }),
    [me],
  )
}

// Desktop-only chrome renders only on desktop, so drag ids never exist twice.
export { useIsDesktop }

function syncInfo(s: ReturnType<typeof useSyncStatus>) {
  if (!s.online) return { ok: false, color: 'bg-faint', label: 'Offline', detail: 'Changes are saved on this device and sync when you’re back online.' }
  if (!s.connected) return { ok: false, color: 'bg-warn', label: 'Connecting…', detail: 'Changes are saved on this device and sync as soon as it connects.' }
  return { ok: true, color: 'bg-ok', label: 'Synced', detail: 'Connected — every change reaches both phones right away.' }
}

/** Phones' top bars: the Connections button, only while something's off (offline / connecting). */
export function SyncDot() {
  const info = syncInfo(useSyncStatus())
  if (info.ok) return null
  return <StatusButton />
}

export function AppShell({ children }: { children: ReactNode }) {
  useEffect(startNewMarks, [])
  const loc = useLocation()
  const kbOpen = useKeyboardOpen()
  const editing = useAnyEditing()
  const desktop = useIsDesktop()
  const me = useMe()
  // While typing on a touch screen the bottom bar steps aside (it never rides the keyboard).
  const touch = useTouchDevice()
  const hideTabs = kbOpen || (editing && touch)
  const settings = loc.route.name === 'settings' ? loc.route.section : null
  const tab = useActiveTab()
  const home = tab === 'board' && isTabRoute(loc.route)

  return (
    <TreeDndProvider>
      <div className="min-h-dvh bg-bg" style={desktop ? { paddingLeft: 'var(--sidebar-w)' } : undefined}>
        {desktop && (
          <nav className="fixed inset-y-0 left-0 z-30 flex flex-col bg-bg px-2.5 py-4" style={{ width: 'var(--sidebar-w)' }} aria-label="Main" data-rail>
            <SidebarResizer />
            <div className="mb-3 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="flex h-9 min-w-0 items-center gap-2 rounded-lg pr-2 pl-1.5 text-[0.9375rem] font-semibold tracking-tight hover:bg-surface-2"
                aria-label="Terrazzo — go to Home"
              >
                <Logo size={22} className="flex-none rounded-md" />
                Terrazzo
              </button>
            </div>
            <RailLink active={home} onClick={() => navigate('/home')} icon={<IconBoard size={17} />} label="Home" />
            <NotesRailLink active={loc.route.name === 'notes'} />
            <RailLink active={loc.route.name === 'search'} onClick={() => navigate('/search')} icon={<IconSearch size={17} />} label="Search" hint="/" />
            <RailLink active={loc.route.name === 'chat'} onClick={() => navigate('/chat')} icon={<IconChat size={17} />} label="Chat" />
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
              <PagesTree onOpen={navigate} dnd />
            </div>
            <ActivityRailLink active={loc.route.name === 'activity'} />
            <RailLink active={settings === 'archive'} onClick={() => navigate('/settings/archive')} icon={<IconArchive size={17} />} label="Archive" />
            <RailLink active={settings === 'trash'} onClick={() => navigate('/settings/trash')} icon={<IconTrash size={17} />} label="Trash" />
            <RailLink
              active={settings !== null && settings !== 'archive' && settings !== 'trash'}
              onClick={() => navigate('/settings')}
              icon={<IconSettings size={17} />}
              label={me.user?.name?.split(' ')[0] ?? 'Settings'}
            />
            {/* The sync dot, then Undo | Redo boxed as on phones (SPEC §7.10); a narrow sidebar keeps just their icons. */}
            <div className="@container flex items-center gap-1.5 pt-2">
              <StatusButton className="h-9 w-9" />
              <UndoRedo />
            </div>
          </nav>
        )}

        <main className="mx-auto w-full max-w-[100rem] pb-[calc(var(--bar-top)+1.5rem)] md:pb-10">{children}</main>

        {!desktop && !hideTabs && <PhoneBar home={home} route={loc.route.name} />}
        <Toasts />
        <SheetHost />
        <ChartDefs />
      </div>
    </TreeDndProvider>
  )
}

/**
 * Phones: the bar floats over the page (Home · Notes · Pages · Chat, no words), with the + beside
 * it on every screen: a tap makes what this screen makes (on Chat, a card on Home), a hold opens
 * the command box (Activity, with its new dot, Undo, Archive, Trash and Settings are at its top).
 */
function PhoneBar({ home, route }: { home: boolean; route: string }) {
  return (
    <div className="dock pointer-events-none fixed inset-x-0 z-30 flex items-center justify-center gap-2.5 px-4" style={{ bottom: 'calc(var(--safe-b) + 0.75rem)' }}>
      <nav className="bar-glass pointer-events-auto flex h-15 items-center gap-0.5 rounded-full px-1.5" aria-label="Main">
        <TabButton active={home} onClick={() => navigate('/home')} icon={<IconBoard size={22} />} label="Home" />
        <NotesTab active={route === 'notes'} />
        <PagesTab active={route === 'pages'} />
        <TabButton active={route === 'chat'} onClick={() => navigate('/chat')} icon={<IconChat size={22} />} label="Chat" />
      </nav>
      <AddButton onClick={runAddButton} className="pointer-events-auto relative h-15 w-15 shadow-lg" />
    </div>
  )
}

/**
 * Phones: the bar's Pages and Notes buttons are also drop targets. While something they take is
 * being dragged, a dashed circle shows it's ready (and says what a drop does); over it, the
 * circle fills and the item drops in — as a page, or back to being a note.
 */
function DropTab({
  zone,
  accepts,
  active,
  onClick,
  icon,
  label,
  dropLabel,
}: {
  zone: string
  accepts: boolean
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
  dropLabel: string
}) {
  const st = useTreeDnd()?.state
  const dragging = !!st?.activeId && accepts
  const { setNodeRef, isOver } = useDroppable({ id: zone, data: { type: 'zone' }, disabled: !dragging })
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`relative ${tabClass(active && !dragging)} ${dragging ? 'opacity-100' : ''}`}
    >
      <span className={`relative flex h-6 w-6 items-center justify-center transition-transform duration-150 ${isOver ? '-translate-y-2' : ''}`}>
        {dragging && (
          <span
            aria-hidden="true"
            className={`absolute rounded-full transition-all duration-150 ${isOver ? '-inset-4 bg-[var(--dock-fg)] shadow-lg' : '-inset-[0.4375rem] animate-pulse border-2 border-dashed border-current opacity-60'}`}
          />
        )}
        <span className={`relative ${isOver ? 'text-[var(--dock-bg)]' : ''}`}>{icon}</span>
      </span>
      {active && !dragging && <DockLine />}
      {dragging && <span className="lab pointer-events-none absolute bottom-full mb-5 bg-fg px-2 py-0.5 whitespace-nowrap text-bg shadow">{isOver ? 'Drop' : dropLabel}</span>}
    </button>
  )
}

/** What the Notes drop targets take: pages, cards and rows (not tabs) — anywhere but Notes itself. */
function useNotesDropAccepts(): boolean {
  const st = useTreeDnd()?.state
  const onNotes = useLocation().route.name === 'notes'
  return !onNotes && !!st && (st.kind === 'page' || st.kind === 'card' || st.kind === 'row')
}

function PagesTab({ active }: { active: boolean }) {
  const st = useTreeDnd()?.state
  const accepts = !!st && st.kind !== 'page' && st.kind !== 'tab'
  return <DropTab zone="zone:pages" accepts={accepts} active={active} onClick={() => navigate('/pages')} icon={<IconPages size={22} />} label="Pages" dropLabel="Make page" />
}

function NotesTab({ active }: { active: boolean }) {
  const accepts = useNotesDropAccepts()
  return <DropTab zone="zone:notes" accepts={accepts} active={active} onClick={() => navigate('/notes')} icon={<IconNotes size={22} />} label="Notes" dropLabel="Make note" />
}

/** Desktop: the sidebar's Notes link takes drops too (a page, card or row becomes a note). */
function NotesRailLink({ active }: { active: boolean }) {
  const accepts = useNotesDropAccepts()
  const dragging = !!useTreeDnd()?.state.activeId && accepts
  const { setNodeRef, isOver } = useDroppable({ id: 'zone:notes', data: { type: 'zone' }, disabled: !dragging })
  return (
    <div ref={setNodeRef} className={`rounded-lg ${isOver ? 'bg-surface-3 ring-2 ring-fg' : dragging ? 'ring-1 ring-line-2 ring-inset' : ''}`}>
      <RailLink active={active} onClick={() => navigate('/notes')} icon={<IconNotes size={17} />} label={isOver ? 'Drop to make it a note' : 'Notes'} />
    </div>
  )
}

/**
 * The sidebar's right edge: drag to resize (180–420 px, remembered on this device),
 * double-click to reset, or focus it and use ← / →.
 */
function SidebarResizer() {
  const dragging = useRef(false)
  const [active, setActive] = useState(false)
  const end = () => {
    if (!dragging.current) return
    dragging.current = false
    setActive(false)
    document.documentElement.classList.remove('resizing-sidebar')
    setSidebarWidth(sidebarWidth(), true)
  }
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuemin={SIDEBAR_MIN}
      aria-valuemax={SIDEBAR_MAX}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      className="group/resize absolute inset-y-0 -right-1.5 z-10 flex w-3 cursor-col-resize justify-center outline-none"
      onPointerDown={(e) => {
        if (e.button !== 0) return
        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)
        dragging.current = true
        setActive(true)
        document.documentElement.classList.add('resizing-sidebar')
      }}
      onPointerMove={(e) => dragging.current && setSidebarWidth(e.clientX)}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT, true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault()
          setSidebarWidth(sidebarWidth() + (e.key === 'ArrowRight' ? 16 : -16), true)
        }
      }}
    >
      <span className={`h-full w-0.5 transition-colors ${active ? 'bg-fg/40' : 'bg-transparent group-hover/resize:bg-line-2 group-focus-visible/resize:bg-fg/40'}`} />
    </div>
  )
}

/** Desktop: step back and forward through changes (also Cmd/Ctrl+Z, Shift+Cmd/Ctrl+Z). */
function UndoRedo() {
  const u = useUndoState()
  const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+'
  const btn = 'flex h-9 min-w-0 flex-1 items-center justify-center gap-2 border border-fg px-2.5 text-sm enabled:hover:bg-surface-2 disabled:border-line-2 disabled:opacity-40'
  return (
    <>
      <button type="button" className={btn} disabled={!u.canUndo} onClick={() => undo({ toast: true })} aria-label="Undo" title={`Undo${u.undoLabel ? `: ${u.undoLabel}` : ''} (${mod}Z)`}>
        <IconUndo size={16} className="flex-none" />
        <span className="hidden @min-[12.5rem]:inline">Undo</span>
      </button>
      <button type="button" className={btn} disabled={!u.canRedo} onClick={() => redo({ toast: true })} aria-label="Redo" title={`Redo${u.redoLabel ? `: ${u.redoLabel}` : ''} (${mod}Shift+Z)`}>
        <IconRedo size={16} className="flex-none" />
        <span className="hidden @min-[12.5rem]:inline">Redo</span>
      </button>
    </>
  )
}

function ActivityRailLink({ active }: { active: boolean }) {
  const unread = useUnreadActivity()
  return <RailLink active={active} onClick={() => navigate('/activity')} icon={<IconBell size={17} />} label="Activity" hint={unread ? String(unread) : undefined} />
}

// The one you're on: full strength with a short line under it (DockLine); the others dimmed (SPEC §7.10).
const tabClass = (active: boolean) => `relative flex h-[2.875rem] w-14 items-center justify-center transition-opacity ${active ? 'opacity-100' : 'opacity-50'}`

/** The line under the phone bar's button you're on; it glides to the next one as the screen slides. */
const DockLine = () => <span className="dock-line absolute bottom-1.5 left-1/2 h-[1.5px] w-3 -translate-x-1/2 bg-current" aria-hidden="true" />

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} aria-current={active ? 'page' : undefined} className={tabClass(active)}>
      {icon}
      {active && <DockLine />}
    </button>
  )
}

function RailLink({ active, onClick, icon, label, hint }: { active: boolean; onClick: () => void; icon: ReactNode; label: string; hint?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // The one you're on: full ink with a small square beside it (SPEC §7.10).
      className={`relative flex h-9 flex-none items-center gap-2.5 px-2 text-left text-sm ${active ? 'font-medium text-fg before:absolute before:top-1/2 before:-left-0.5 before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:bg-fg' : 'text-muted hover:text-fg'}`}
      aria-current={active ? 'page' : undefined}
    >
      <span className="flex w-5 justify-center">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint && <span className="lab text-[0.6875rem] text-faint">{hint}</span>}
    </button>
  )
}

/**
 * Page header. On tab pages (All, Days, Notes, Done, saved views) it's the tab bar, with
 * the title kept for screen readers; elsewhere a plain title bar.
 */
/** `find`: on a tab page, find-on-this-page sits at the right end of the tab bar (SPEC §7.10). */
export function TopBar({ title, left, right, sub, find }: { title: ReactNode; left?: ReactNode; right?: ReactNode; sub?: ReactNode; find?: FindState }) {
  const { route } = useLocation()
  if (isTabRoute(route)) {
    return (
      <>
        <h1 className="sr-only">{title}</h1>
        <TabBar
          right={
            <>
              {find && <FindButton find={find} />}
              {right}
            </>
          }
          sub={sub}
        />
      </>
    )
  }
  return (
    <header className="sticky top-0 z-20 border-b border-transparent bg-bg/90 backdrop-blur" style={{ paddingTop: 'var(--safe-t)' }}>
      <div className="flex h-12 items-center gap-2 px-4">
        {left}
        <h1 className="min-w-0 flex-1 truncate text-[1.0625rem] font-semibold tracking-tight">{title}</h1>
        <span className="md:hidden">
          <SyncDot />
        </span>
        {right}
      </div>
      {sub}
    </header>
  )
}
